/**
 * `GET /kontroledge/fwaas/:id/overview` (CTX-1091..1096): every section of the Virtual
 * Firewall's simple view, from one FortiManager reading.
 *
 * Each section is `null` on its own when the firewall could not answer it — never a
 * stale or invented number (I5). A `null` *inside* a section is different: it means
 * the firewall has nothing there (a standalone unit has no HA members), not that the
 * reading failed. Nothing here carries an address, a user or a host name; the backend
 * rebuilds every section from a whitelist (data map, P-CTX-95).
 */

export interface UptimeSection {
    /** ISO instant of the last reboot. */
    since: string;
}

export interface Metric {
    now: number | null;
    peak_1h: number | null;
}

export interface ResourcesSection {
    /** Percent. */
    cpu: Metric;
    /** Percent, of `total_mb`. */
    memory: Metric & { total_mb: number | null };
    /** Open sessions. */
    sessions: Metric;
}

export interface FirmwareSection {
    current: string | null;
    /** May be a feature release (FortiOS maturity `F`). */
    newest_available: string | null;
    /** Newest mature release (maturity `M`) — the one worth recommending. */
    newest_mature: string | null;
}

export interface HaSection {
    mode: "standalone" | "active-passive" | "active-active" | string;
    members_up: number | null;
    members_total: number | null;
    in_sync: boolean | null;
    last_failover_at: string | null;
}

export type LicenseServiceId = "appctrl" | "ips" | "antivirus" | "web_filtering";

export interface LicenseService {
    id: LicenseServiceId | string;
    status: "active" | "not_in_plan" | "expired";
    /** Calendar date, `YYYY-MM-DD`. */
    expires_on: string | null;
}

export interface LicensesSection {
    vm: { status: string | null; expires_on: string | null };
    support: { level: string | null; expires_on: string | null };
    services: LicenseService[];
    security_updates: "connected" | "disconnected" | string | null;
}

export interface FwaasOverview {
    uptime: UptimeSection | null;
    resources: ResourcesSection | null;
    firmware: FirmwareSection | null;
    ha: HaSection | null;
    licenses: LicensesSection | null;
    /** When the firewall was read (CTX-1207): earlier than now on a cache hit, null when it was not. */
    read_at: string | null;
}

/** What the page shows when the overview could not be read at all. */
export const NO_OVERVIEW: FwaasOverview = Object.freeze({
    uptime: null,
    resources: null,
    firmware: null,
    ha: null,
    licenses: null,
    read_at: null,
});
